import React, { Component } from "react";
import { connect } from "react-redux";
import { Container, Row, Col, Button, Form, Spinner, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getCompanyByID, createCompany, updateCompany } from '../../actions/companyAction';

class CompanyEdit extends Component {
  state = {
    loading: false,
    saving: false,
    company: {
      company_id: '',
      parent_company_id: '',
      name: '',
      legal_name: '',
      company_shortcode: '',
      description: '',
      address: '',
      tel: '',
      website: '',
      logo: '',
      is_seller: false,
      is_factory: false,
      is_shipping_agency: false,
      is_system: false,
      metadata: {}
    },
    errors: {}
  };

  componentDidMount() {
    // Use companyId prop if in modal mode, otherwise use match params
    const id = this.props.companyId || this.props.match.params.id;
    if (id && id !== 'create') {
      this.loadCompany(id);
    }
  }

  loadCompany = async (id) => {
    this.setState({ loading: true });
    try {
      const response = await this.props.getCompanyByID(id);
      if (response && response.data) {
        this.setState({ 
          company: {
            ...this.state.company,
            ...response.data
          }
        });
      }
    } catch (error) {
      console.error('Error loading company:', error);
      alert('Lỗi khi tải thông tin công ty: ' + (error.response?.data?.message || error.message));
    } finally {
      this.setState({ loading: false });
    }
  };

  handleChange = (field, value) => {
    this.setState(prevState => ({
      company: {
        ...prevState.company,
        [field]: value
      },
      errors: {
        ...prevState.errors,
        [field]: null
      }
    }));
  };

  handleCheckboxChange = (field) => {
    this.setState(prevState => ({
      company: {
        ...prevState.company,
        [field]: !prevState.company[field]
      }
    }));
  };

  validateForm = () => {
    const { company } = this.state;
    const errors = {};
    const isEdit = this.props.companyId || (this.props.match.params.id && this.props.match.params.id !== 'create');

    // Company ID validation - only for edit mode (backend auto-generates for create)
    if (isEdit) {
      if (!company.company_id) {
        errors.company_id = 'Company ID là bắt buộc';
      } else if (!/^\d{6}$/.test(company.company_id)) {
        errors.company_id = 'Company ID phải là 6 chữ số';
      }
    }

    if (!company.name) {
      errors.name = 'Tên công ty là bắt buộc';
    }

    // Company shortcode validation
    if (!company.company_shortcode) {
      errors.company_shortcode = 'Company shortcode là bắt buộc';
    } else if (!/^[A-Z0-9]+$/.test(company.company_shortcode)) {
      errors.company_shortcode = 'Company shortcode chỉ được chứa chữ hoa và số';
    } else if (company.company_shortcode.length < 2 || company.company_shortcode.length > 20) {
      errors.company_shortcode = 'Company shortcode phải từ 2-20 ký tự';
    }

    // Parent company ID validation
    if (company.parent_company_id && !/^\d{6}$/.test(company.parent_company_id)) {
      errors.parent_company_id = 'Parent Company ID phải là 6 chữ số';
    }

    // At least one type must be selected
    if (!company.is_seller && !company.is_factory && !company.is_shipping_agency && !company.is_system) {
      errors.types = 'Phải chọn ít nhất một loại công ty';
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!this.validateForm()) {
      return;
    }

    this.setState({ saving: true });
    
    try {
      const { id } = this.props.match.params;
      const { company } = this.state;
      
      // Remove empty string values
      const cleanCompany = Object.fromEntries(
        Object.entries(company).map(([key, value]) => [
          key,
          value === '' ? undefined : value
        ])
      );

      if (id && id !== 'create') {
        // Update
        await this.props.updateCompany(id, cleanCompany);
        alert('Cập nhật công ty thành công!');
      } else {
        // Create - remove company_id since backend auto-generates it
        const { company_id, ...createData } = cleanCompany;
        await this.props.createCompany(createData);
        alert('Tạo công ty thành công!');
      }
      
      // Check if we're in modal mode
      if (this.props.onHide) {
        this.props.onHide(); // Close modal
      } else {
        this.props.history.push('/admin/company-management'); // Navigate normally
      }
    } catch (error) {
      console.error('Error saving company:', error);
      alert('Lỗi khi lưu công ty: ' + (error.response?.data?.message || error.message));
    } finally {
      this.setState({ saving: false });
    }
  };

  render() {
    const { loading, saving, company, errors } = this.state;
    const { id } = this.props.match.params;
    const isEdit = id && id !== 'create';

    if (loading) {
      return (
        <Container fluid className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2">Đang tải...</div>
        </Container>
      );
    }

    return (
      <Container fluid>
        {!this.props.onHide && (
          <Row className="mb-3">
            <Col>
              <h2>{isEdit ? 'Chỉnh sửa công ty' : 'Thêm công ty mới'}</h2>
            </Col>
            <Col className="text-end">
              <Button variant="secondary" as={Link} to="/admin/company-management">
                Quay lại danh sách
              </Button>
            </Col>
          </Row>
        )}

        <Form onSubmit={this.handleSubmit}>
          <Row>
            <Col md={8}>
              <Card className="mb-4">
                <Card.Header>Thông tin cơ bản</Card.Header>
                <Card.Body>
                  <Row>
                    {isEdit && (
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Company ID</Form.Label>
                          <Form.Control
                            type="text"
                            value={company.company_id}
                            readOnly
                            disabled
                            style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                          />
                          <Form.Text className="text-muted">
                            Company ID được tạo tự động bởi hệ thống
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    )}
                    <Col md={isEdit ? 6 : 12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Parent Company ID</Form.Label>
                        <Form.Control
                          type="text"
                          value={company.parent_company_id}
                          onChange={(e) => this.handleChange('parent_company_id', e.target.value)}
                          isInvalid={!!errors.parent_company_id}
                          placeholder="Nhập 6 chữ số (tùy chọn)"
                          maxLength={6}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.parent_company_id}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Tên công ty <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={company.name}
                      onChange={(e) => this.handleChange('name', e.target.value)}
                      isInvalid={!!errors.name}
                      placeholder="Nhập tên hiển thị"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Tên pháp lý</Form.Label>
                    <Form.Control
                      type="text"
                      value={company.legal_name}
                      onChange={(e) => this.handleChange('legal_name', e.target.value)}
                      placeholder="Nhập tên pháp lý (tùy chọn)"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Mã công ty <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={company.company_shortcode}
                      onChange={(e) => this.handleChange('company_shortcode', e.target.value.toUpperCase())}
                      isInvalid={!!errors.company_shortcode}
                      placeholder="VD: KSCRESIN, SE, BREAKWOOD"
                      maxLength={20}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.company_shortcode}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Chỉ được sử dụng chữ hoa và số, 2-20 ký tự
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Mô tả</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={company.description}
                      onChange={(e) => this.handleChange('description', e.target.value)}
                      placeholder="Nhập mô tả về công ty"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Địa chỉ</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={company.address}
                      onChange={(e) => this.handleChange('address', e.target.value)}
                      placeholder="Nhập địa chỉ (tùy chọn)"
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Số điện thoại</Form.Label>
                        <Form.Control
                          type="text"
                          value={company.tel}
                          onChange={(e) => this.handleChange('tel', e.target.value)}
                          placeholder="Nhập số điện thoại (tùy chọn)"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Website</Form.Label>
                        <Form.Control
                          type="url"
                          value={company.website}
                          onChange={(e) => this.handleChange('website', e.target.value)}
                          placeholder="https://example.com (tùy chọn)"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Logo URL</Form.Label>
                    <Form.Control
                      type="url"
                      value={company.logo}
                      onChange={(e) => this.handleChange('logo', e.target.value)}
                      placeholder="https://example.com/logo.png (tùy chọn)"
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="mb-4">
                <Card.Header>
                  Loại công ty <span className="text-danger">*</span>
                  {errors.types && <div className="text-danger small mt-1">{errors.types}</div>}
                </Card.Header>
                <Card.Body>
                  <Form.Check
                    type="checkbox"
                    label="Seller (Người bán)"
                    checked={company.is_seller}
                    onChange={() => this.handleCheckboxChange('is_seller')}
                    className="mb-2"
                  />
                  <Form.Check
                    type="checkbox"
                    label="Factory (Nhà máy)"
                    checked={company.is_factory}
                    onChange={() => this.handleCheckboxChange('is_factory')}
                    className="mb-2"
                  />
                  <Form.Check
                    type="checkbox"
                    label="Shipping Agency (Đơn vị vận chuyển)"
                    checked={company.is_shipping_agency}
                    onChange={() => this.handleCheckboxChange('is_shipping_agency')}
                    className="mb-2"
                  />
                  <Form.Check
                    type="checkbox"
                    label="System (Vận hành hệ thống)"
                    checked={company.is_system}
                    onChange={() => this.handleCheckboxChange('is_system')}
                  />
                </Card.Body>
              </Card>

              <div className="d-grid gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={saving}
                  size="lg"
                >
                  {saving ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Đang lưu...
                    </>
                  ) : (
                    isEdit ? 'Cập nhật' : 'Tạo mới'
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Container>
    );
  }
}

const mapStateToProps = (state) => ({
  currentCompany: state.companyReducer.currentCompany
});

export default connect(mapStateToProps, {
  getCompanyByID,
  createCompany,
  updateCompany
})(CompanyEdit);
